import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissionsOptional } from '../contexts/PermissionsContext';
import { Spinner, Container } from 'react-bootstrap';

// `grant`: a per-user extra permission key (server/utils/userGrants.js) that
// also opens this route for a plain `user` an admin granted it to.
const ProtectedRoute = ({ children, requiredRoles = [], allowedRoles = [], allowedEmails = [], grant = null }) => {
    const { user, loading, isAuthenticated, hasAnyRole } = useAuth();
    const { ready: permissionsReady, hasRoleOrGrant } = usePermissionsOptional();
    const location = useLocation();

    // Support both requiredRoles and allowedRoles for consistency
    const rolesToCheck = requiredRoles.length > 0 ? requiredRoles : allowedRoles;

    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </Container>
        );
    }

    if (!isAuthenticated()) {
        // Redirect to login page with the current location as the return URL
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Check if user has required roles
    // A `user` who may hold the grant: wait for the permissions read rather than
    // flashing "Access Denied" on a screen they are about to be allowed into.
    if (rolesToCheck.length > 0 && !hasAnyRole(rolesToCheck) && grant && user?.role === 'user' && !permissionsReady) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </Container>
        );
    }

    if (rolesToCheck.length > 0 && !hasAnyRole(rolesToCheck) && !(grant && hasRoleOrGrant([], grant))) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                <div className="text-center">
                    <h3>Access Denied</h3>
                    <p>You don't have permission to access this page.</p>
                </div>
            </Container>
        );
    }

    // Check if user email is allowed when provided
    if (allowedEmails.length > 0 && (!user?.email || !allowedEmails.includes(user.email))) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                <div className="text-center">
                    <h3>Access Denied</h3>
                    <p>You don't have permission to access this page.</p>
                </div>
            </Container>
        );
    }

    return children;
};

export default ProtectedRoute; 